import React, { useEffect, useState } from 'react'
import { Box, Typography, Button } from '@mui/material'
import colors from 'styles/colors'
import RowStack from 'components/atoms/container/RowStack'
import InCardStack, { ColStack } from 'components/atoms/container/InCardStack'
import { RenderSummary } from 'components/molecules/myTeam/meeting/SummaryTabs'
import {
  PrevSummaryNotification,
  TranscriptMenu,
  OpenEventInNewTab
} from 'components/molecules/myTeam/meeting/SummaryMolecules'
import { getRequest } from 'utils/api'
import { USER_CALENDAR_EVENTS_URL } from 'services/constants'
import StyledAccordion from 'components/atoms/container/Accordion'
import { eventTitle } from 'utils/eventUtils'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

const PastMeetingSummary = ({ event, connection }) => {
  const { t } = useTranslation()
  const [pagination, setPagination] = useState({ next: 1, total: 0 })
  const [pastEvents, setPastEvents] = useState([])
  const [activeIndex, setActiveIndex] = useState(0)
  const loadTime = moment.utc().toISOString()
  const EVENTS_PER_GROUP = 10
  const [nbVisibleEvents, setNbVisibleEvents] = useState(EVENTS_PER_GROUP)

  const paginatePrevEvents = async (
    next = pagination.next,
    prevPastEvents = pastEvents
  ) => {
    if (next) {
      const res = await getRequest(USER_CALENDAR_EVENTS_URL, {
        limit: EVENTS_PER_GROUP,
        page: next,
        historicalEvents: true,
        maxDateTime: loadTime,
        userConnectionEventId: event?.userConnectionEventId
      })
      if (res.status === 200) {
        let total = res.data.data.pagination?.total

        const filteredEvents = [
          ...prevPastEvents,
          ...res.data.data.data
        ].filter(e => e.id !== event.id)
        setPastEvents(filteredEvents)

        setPagination({ ...res.data.data.pagination, total })
      }
    }
  }

  const increaseVisibleEvents = async () => {
    const newNum = nbVisibleEvents + EVENTS_PER_GROUP
    await paginatePrevEvents()
    setNbVisibleEvents(Math.min(pagination?.total, newNum))
  }

  useEffect(() => {
    paginatePrevEvents(1, [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id])

  const getSummaryComponent = (e, isActive) => {
    const startTime = moment(e?.startTime)
    const day = startTime?.format('DD')
    const month = startTime?.format('MMM').toUpperCase()
    const strStartTime = startTime?.format('h:mm a')
    const strCeaseTime = e?.endTime ? moment(e.endTime)?.format('h:mm a') : null
    return (
      <RowStack spacing={0} justifyContent='space-between'>
        <RowStack spacing={8}>
          <InCardStack spacing={1}>
            <Typography variant='h3' fontSize={22} lineHeight={'120%'}>
              {day}
            </Typography>
            <Typography
              variant='eyebrow'
              fontSize={14}
              fontWeight={700}
              lineHeight={'100%'}>
              {month}
            </Typography>
          </InCardStack>

          <RowStack spacing={6} width='auto'>
            <ColStack spacing={2} sx={{ overflow: 'hidden' }}>
              <RowStack spacing={4}>
                <Typography
                  variant='h4'
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textWrap: 'nowrap',
                    textAlign: 'left'
                  }}>
                  {eventTitle(connection, t)}
                </Typography>

                <OpenEventInNewTab event={e} />
              </RowStack>
              <Typography
                variant='formTagText'
                sx={{
                  fontWeight: 400,
                  lineHeight: '100%',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'pre-line',
                  textAlign: 'left'
                }}>
                {`${strStartTime} - ${strCeaseTime}`}
              </Typography>
            </ColStack>
          </RowStack>
        </RowStack>

        {isActive && e.transcript && (
          <Box
            onClick={e => {
              e.stopPropagation()
            }}>
            <TranscriptMenu event={e} connection={connection} />
          </Box>
        )}
      </RowStack>
    )
  }

  return (
    <ColStack spacing={8}>
      {pastEvents.length > 0 ? (
        <>
          <Typography variant='h4'>{t('myTeam.latestMeeting')}</Typography>
          <ColStack spacing={6}>
            {pastEvents.map((pEvent, index) => {
              const isActive = index === activeIndex
              return (
                <>
                  {index === 1 && (
                    <Typography variant='eyebrow' color={'textSecondary'}>
                      {t('myTeam.previousMeetings')}
                    </Typography>
                  )}
                  <StyledAccordion
                    key={`past-${pEvent.id}`}
                    initialExpanded={isActive}
                    expandCallback={state => {
                      if (state) setActiveIndex(index)
                      else setActiveIndex(-1)
                    }}
                    summaryComponent={getSummaryComponent(pEvent, isActive)}
                    sx={{
                      paddingX: 8,
                      paddingY: 6,
                      borderRadius: '8px !important',
                      border: isActive
                        ? `1px solid ${colors.GoldenYellow100}`
                        : 'none',
                      backgroundColor: isActive
                        ? '#FFFCF6'
                        : colors.GoldenYellow100,
                      '&.Mui-expanded': {
                        marginBottom: 0,
                        padding: 8,
                        paddingBottom: 12
                      },
                      '& .MuiAccordionSummary-root': {
                        minHeight: 'auto !important'
                      },
                      '& .MuiAccordionSummary-content': {
                        margin: '0 !important'
                      },
                      ':before': { display: 'none' }
                    }}>
                    <Box marginTop={8}>
                      {pEvent.summary ? (
                        <RenderSummary
                          event={pEvent}
                          connection={connection}
                          isPreviousMeeting={true}
                        />
                      ) : (
                        <PrevSummaryNotification isPrevSummary={true} />
                      )}
                    </Box>
                  </StyledAccordion>
                </>
              )
            })}

            {pagination?.total !== 0 && (
              <Button
                onClick={increaseVisibleEvents}
                disabled={pagination?.total <= nbVisibleEvents}
                variant='outlined'
                size='small'
                color='primary'>
                {t('common.loadMore')}
              </Button>
            )}
          </ColStack>
        </>
      ) : (
        <PrevSummaryNotification hasTranscript={!!event?.transcript} />
      )}
    </ColStack>
  )
}

export default PastMeetingSummary
